/* eslint react-hooks/exhaustive-deps: "error" */
import React from 'react';
import { Scrollbars, positionValues } from 'react-custom-scrollbars';
import _debounce from 'lodash/debounce';

import { PrimaryButton, colors, Loader } from '@crehana/ui';
import { NewArrowDown } from '@crehana/icons';

import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';

import { Me } from 'Jsx/Types/Me';
import { ChatNode } from 'Jsx/CrehanaLive/graphql/types/ChatNode';
import { ChatActions } from 'Jsx/CrehanaLive/hooks/useChatActions';

import TabWrapper from '../TabWrapper';

import TextFieldForm from './components/TextFieldForm';
import MessageCard from './components/MessageCard';
import PinnedMessage from './components/PinnedMessage';

interface ChatProps extends ChatActions {
  user?: Me;
  loadingUser: boolean;
  error: any;
  loadingInbox: boolean;
  messages: ChatNode[];
  chatPinned?: ChatNode;
  isPusherConnected: boolean;
  isPusherSubscriptionSucceeded: boolean;
  hasModeratorPermission: boolean;
  isLoggedIn: boolean;
  canSendMessages?: boolean;
  refetchInboxData: () => void;
}

// We need a flag to know if the user already read the message
// it is useful form new messages not for already sent messages
// (like when the user hits the url for the first time)
type Message = ChatNode & { seen: boolean };

const Chat: React.FC<ChatProps> = ({
  user,
  error,
  loadingInbox,
  messages: inboxMessages,
  chatPinned,
  isPusherConnected,
  isPusherSubscriptionSucceeded,
  hasModeratorPermission,
  isLoggedIn,
  canSendMessages,
  refetchInboxData,
  sendMessage,
  setMessageAsQuestion,
  hideMessage,
  setPinnedMessage,
  unpinMessage,
  blockUser,
  isChatBlocked,
}) => {
  const { t } = useCrehanaLiveTranslations();
  const scrollerRef = React.useRef<Scrollbars | null>(null);
  // scrollDelta represents the percentual scroll diference where the top
  // is 0 and the bottom is 1
  const [isAtLatestMessage, setIsAtLatestMessage] = React.useState(true);
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [replyUser, setReplyUser] = React.useState<string>();

  const unseenMessages = React.useMemo(
    () => messages.filter(({ seen }) => !seen).length,
    [messages],
  );

  const scrollToBottom = () => {
    if (
      scrollerRef &&
      scrollerRef.current &&
      scrollerRef.current.scrollToBottom
    ) {
      scrollerRef.current.scrollToBottom();
    }
  };

  React.useEffect(() => {
    // Force scroll to bottom on first render
    scrollToBottom();
  }, []);

  React.useEffect(() => {
    // scroll to bottom when the last link-preview has been rendered
    // we need it because the rendering of link-preview is async...
    // ...and it can come in anytime and push to top the content of the chat
    document.addEventListener('onLinkPreview', scrollToBottom);

    return () => {
      document.removeEventListener('onLinkPreview', scrollToBottom);
    };
  }, []);

  React.useEffect(() => {
    if (inboxMessages.length) {
      setMessages(
        prev =>
          inboxMessages.map((message, i) => ({
            ...message,
            seen:
              prev[i]?.seen ||
              isAtLatestMessage ||
              message.user?.originalId === user?.originalId,
          })) || prev,
      );
    }
  }, [inboxMessages, isAtLatestMessage, messages.length, user]);

  React.useEffect(() => {
    if (!unseenMessages) {
      scrollToBottom();
    }
  }, [messages.length, unseenMessages, isPusherConnected]);

  const updateScrollDelta = _debounce(
    (value: positionValues) =>
      setIsAtLatestMessage(
        value.scrollHeight > value.clientHeight ? value.top >= 0.98 : true,
      ),
    350,
  );

  const onSubmitMessage = (message: string) => {
    sendMessage(message);
    setReplyUser(undefined);
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full p-16">
        <p className="text-white font-body3">{t('UPS_ERROR_CHAT_LIVE')}</p>
        <br />
        <PrimaryButton
          label={t('RETRY_AGAIN_LABEL')}
          onClick={() => refetchInboxData()}
          darkMode
        />
      </div>
    );
  }

  if (loadingInbox) {
    return (
      <div className="w-full h-full">
        <TabWrapper className="flex flex-col items-center justify-center py-32">
          <Loader white />
          <p className="block w-full mt-16 text-center text-white font-h4">
            {t('LOAD_CHAT')}
          </p>
          <p className="mt-16 text-white font-body3">
            {t('LOAD_CHAT_MESSAGE')}
          </p>
        </TabWrapper>
      </div>
    );
  }

  const renderChatInput = () => {
    if (!isLoggedIn) {
      return (
        <div className="flex items-center justify-center w-full h-full">
          <button
            className="tw-font-semibold cursor-pointer font-body4 text-white"
            type="button"
            onClick={() => {
              window.showRegisterModal(
                null,
                undefined,
                undefined,
                'Lives - Chat',
              );
            }}
          >
            {t('JOIN_CHAT')}
            <span className="text-secondary-light">{t('SIGN_UP')}</span>
          </button>
        </div>
      );
    }

    if (isChatBlocked) {
      return (
        <div className="flex items-center justify-center w-full h-full text-center">
          <p className="font-body4 text-base-lighter">
            {t('LOCKED_RULES')}
            <a
              href="https://ayuda.crehana.com/hc/es/articles/360047299453"
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondary-light"
            >
              {t('RULES_COMUNITY')}
            </a>
            {t('NOT_LIVE')}
          </p>
        </div>
      );
    }
    return (
      <div className="h-full p-0 md:px-24 md:pt-8 md:pb-32">
        <TextFieldForm
          replyUser={replyUser}
          setReplyUser={setReplyUser}
          onSubmit={onSubmitMessage}
        />
      </div>
    );
  };

  const showTextField =
    canSendMessages && (isPusherConnected || isPusherSubscriptionSucceeded);

  return (
    <div className="flex flex-col justify-between w-full h-full">
      <TabWrapper
        full={!showTextField}
        className="relative self-stretch flex-shrink-0"
      >
        {chatPinned ? (
          <PinnedMessage
            key={chatPinned.createdDate}
            chatPinned={chatPinned}
            areActionsEnabled={canSendMessages}
            hasModeratorPermission={hasModeratorPermission}
            unpinMessage={unpinMessage}
          />
        ) : null}
        <Scrollbars
          ref={scrollBarsInstance => {
            scrollerRef.current = scrollBarsInstance;
          }}
          onUpdate={updateScrollDelta}
          className="w-full h-full"
        >
          <div className="flex flex-row flex-wrap h-full px-24 zIndex-10">
            <p className="block w-full py-12 pointer-events-none font-body4 text-base-lighter">
              {t('WELCOME_TO_CHAT')}
            </p>
            <div className="self-end w-full">
              {messages.slice(-146).map(inbox => (
                <MessageCard
                  {...inbox}
                  key={inbox.originalId!}
                  areActionsEnabled={canSendMessages}
                  hasModeratorPermission={hasModeratorPermission}
                  setMessageAsQuestion={setMessageAsQuestion}
                  setPinnedMessage={setPinnedMessage}
                  onReplyClick={setReplyUser}
                  hideMessage={hideMessage}
                  isLoggedIn={isLoggedIn}
                  // Disable block button for other moderators/speakers
                  blockUser={
                    !inbox.isModerator || inbox.isPresenter
                      ? blockUser
                      : undefined
                  }
                />
              ))}
            </div>
          </div>
        </Scrollbars>
        {unseenMessages ? (
          <div className="absolute bottom-0 left-0 right-0 px-20 py-8 text-center">
            <button
              type="button"
              onClick={scrollToBottom}
              className="w-full py-8 text-center outline-none bg-purple-lighter max-w-280"
              style={{ borderRadius: '5px' }}
            >
              <p className="inline-flex items-center text-white font-subtitle4">
                <NewArrowDown size={16} color={colors.white} className="mr-8" />
                {t('SEE_NEW_MESSAGES')} ({unseenMessages})
              </p>
            </button>
          </div>
        ) : null}
      </TabWrapper>

      {showTextField ? (
        <div
          className="relative bg-base-lighter-8 h-52 md:h-96"
          data-testid="chat-input-area"
        >
          {renderChatInput()}
        </div>
      ) : null}
    </div>
  );
};

export default Chat;
